import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { fetchImportTargets, previewImport, runImport, type ImportField, type ImportPreview } from './importApi'
import type { ImportReport } from './types'

/**
 * Импорт CSV с сопоставлением колонок (ТЗ §5.1, тикеты #48, #107).
 *
 * Файл из другой системы не обязан знать наши имена полей: его «Ответственный менеджер» —
 * наш «Ответственный» только потому, что так скажет человек. Поэтому сначала предпросмотр
 * (заголовки и первые строки), потом сопоставление «поле → колонка файла» — с подсказкой
 * там, где заголовок явно совпал, — и только потом загрузка.
 *
 * Отчёт показывается **построчно и целиком**: «загружено 1900 из 2000» без списка строк
 * отправляет искать сотню пропавших вручную. Частичный успех — отдельное состояние, а не
 * ошибка и не успех.
 */

interface ImportPanelProps {
  /** Цель импорта: сущность списка или своя (`sales.task`). */
  target: string
  onImported: () => void
  /** Раскрыть сразу — когда панель открыта пунктом меню «Импорт…», второй клик лишний. */
  open?: boolean
}

export function ImportPanel({ target, onImported, open }: ImportPanelProps) {
  const { t } = useApp()
  const targets = useQuery({ queryKey: ['import-targets'], queryFn: ({ signal }) => fetchImportTargets(signal) })
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<ImportPreview | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [report, setReport] = useState<ImportReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Not offered where the server would refuse: no target, or no create grant on it.
  if (!(targets.data ?? []).some((candidate) => candidate.key === target)) {
    return null
  }

  async function choose(chosen: File) {
    setBusy(true)
    setError(null)
    setReport(null)
    setPreview(null)
    try {
      const result = await previewImport(target, chosen)
      setFile(chosen)
      setPreview(result)
      setMapping(result.suggested)
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setBusy(false)
    }
  }

  async function run() {
    if (!file) return
    setBusy(true)
    setError(null)
    try {
      const result = await runImport(target, file, mapping)
      setReport(result)
      // Re-read the list on a partial success too: the rows that landed are already there.
      if (result.imported > 0) onImported()
    } catch (cause) {
      setError((cause as Error).message)
    } finally {
      setBusy(false)
    }
  }

  const title = (field: ImportField) => field.label ?? t(`column.${field.name}`)
  const unmapped = (preview?.fields ?? []).filter((field) => field.required && !mapping[field.name])

  return (
    <details className="import-panel" open={open}>
      <summary>{t('import.title')}</summary>
      <input
        type="file"
        accept=".csv,text/csv"
        disabled={busy}
        aria-label={t('import.file')}
        onChange={(event) => {
          const chosen = event.target.files?.[0]
          if (chosen) void choose(chosen)
          // Reset, or choosing the same file again fires no change and the button looks broken.
          event.target.value = ''
        }}
      />

      {preview && (
        <div className="import-mapping">
          <p className="muted">{t('import.found', { rows: preview.rows, columns: preview.header.length })}</p>
          <table className="card-table" aria-label={t('import.mapping')}>
            <thead>
              <tr>
                <th>{t('import.field')}</th>
                <th>{t('import.fileColumn')}</th>
                <th>{t('import.example')}</th>
              </tr>
            </thead>
            <tbody>
              {preview.fields.map((field) => {
                const header = mapping[field.name] ?? ''
                const position = preview.header.indexOf(header)
                return (
                  <tr key={field.name}>
                    <td>
                      {title(field)}
                      {field.required && ' *'}
                      {field.hint && <div className="muted">{field.hint}</div>}
                    </td>
                    <td>
                      <select
                        aria-label={`${t('import.fileColumn')}: ${title(field)}`}
                        value={header}
                        onChange={(event) => setMapping({ ...mapping, [field.name]: event.target.value })}
                      >
                        <option value="">{t('import.skip')}</option>
                        {preview.header.map((column) => (
                          <option key={column} value={column}>{column}</option>
                        ))}
                      </select>
                    </td>
                    <td className="muted">{position >= 0 ? (preview.sample[0]?.[position] ?? '') : ''}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {unmapped.length > 0 && (
            <p role="alert">{t('import.unmapped', { fields: unmapped.map(title).join(', ') })}</p>
          )}
          <button type="button" disabled={busy || unmapped.length > 0} onClick={() => void run()}>
            {t('import.run', { rows: preview.rows })}
          </button>
        </div>
      )}

      {busy && <p>{t('import.running')}</p>}
      {error && <p role="alert">{error}</p>}

      {report && (
        <div className="import-report" role="status">
          <p>{t('import.result', { imported: report.imported, total: report.totalRows, failed: report.failed })}</p>
          {report.errors.length > 0 && (
            <table className="import-errors">
              <thead>
                <tr>
                  <th>{t('import.line')}</th>
                  <th>{t('import.column')}</th>
                  <th>{t('import.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {report.errors.map((rowError) => (
                  <tr key={`${rowError.line}-${rowError.column ?? ''}`}>
                    <td>{rowError.line}</td>
                    <td>{rowError.column ?? '—'}</td>
                    <td>{rowError.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </details>
  )
}
